import type {
	BaseBehaviorEventPayload,
	BehaviorPropertySnapshot,
	BehaviorPropertySnapshotSource,
	TrackLeadCreatedInput,
	TrackPropertyViewedInput,
	TrackWishlistBehaviorInput,
	UseBehaviorEventTrackingReturn
} from './types';

import { useLogger } from '~/composables/useLogger';
import { useTrackingIdentity } from '~/composables/visitorIdentity';
import { v4 as uuidv4 } from 'uuid';

import { buildPropertySnapshot } from './buildPropertySnapshot';
import {
	clearOfflineBehaviorViews,
	getOfflineBehaviorViews,
	saveBehaviorViewOffline
} from './offlineStorage';
import {
	hasViewedPropertyThisSession,
	markPropertyViewedThisSession
} from './sessionDedup';

const ENDPOINT = '/api/behavior-events';

function resolveSnapshot(
	property?: BehaviorPropertySnapshotSource | null
): BehaviorPropertySnapshot | undefined {
	if (!property) return undefined;
	return buildPropertySnapshot(property);
}

function sendEvent(
	payload: BaseBehaviorEventPayload,
	log: ReturnType<typeof useLogger>
): void {
	log.debug('Emitting behavior event', {
		eventId: payload.eventId,
		eventType: payload.eventType,
		propertyId: payload.propertyId,
		sessionId: payload.sessionId
	});

	try {
		fetch(ENDPOINT, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
			keepalive: true
		}).catch(() => {
			/* fire-and-forget — tracking failures must never reach the user */
		});
	} catch {
		/* fetch may throw synchronously on invalid input — also swallow */
	}
}

function buildCommonFields(
	eventType: BaseBehaviorEventPayload['eventType'],
	portalUserId: string,
	sessionId: string,
	propertyId: string,
	propertySnapshot?: BehaviorPropertySnapshot,
	occurredAt?: string
): Omit<BaseBehaviorEventPayload, 'payload'> {
	return {
		eventId: uuidv4(),
		eventType,
		occurredAt: occurredAt ?? new Date().toISOString(),
		sessionId,
		portalUserId,
		propertyId,
		...(propertySnapshot ? { propertySnapshot } : {})
	};
}

export function useBehaviorEventTracking(): UseBehaviorEventTrackingReturn {
	const getTrackingIdentity = useTrackingIdentity();
	const { user, loggedIn } = useUserSession();
	const logger = useLogger('BehaviorEventTracking');

	function getPortalUserId(): string | null {
		return user.value?.id ?? null;
	}

	function trackPropertyViewed(input: TrackPropertyViewedInput): void {
		if (!import.meta.client) return;
		if (!input.propertyId) return;
		if (hasViewedPropertyThisSession(input.propertyId)) return;

		const viewType = input.viewType ?? 'detail_page';
		const source = input.source ?? 'property_page';
		const propertySnapshot = resolveSnapshot(input.property);
		const { sessionId } = getTrackingIdentity();
		const occurredAt = new Date().toISOString();

		markPropertyViewedThisSession(input.propertyId);

		const portalUserId = getPortalUserId();
		if (!loggedIn.value || !portalUserId) {
			saveBehaviorViewOffline({
				propertyId: input.propertyId,
				viewType,
				source,
				sessionId,
				occurredAt,
				propertySnapshot
			});
			return;
		}

		sendEvent(
			{
				...buildCommonFields(
					'property_viewed',
					portalUserId,
					sessionId,
					input.propertyId,
					propertySnapshot,
					occurredAt
				),
				payload: { viewType, source }
			},
			logger
		);
	}

	function trackWishlistPropertyAdded(input: TrackWishlistBehaviorInput): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId || !input.wishlistId || !input.propertyId) return;

		const { sessionId } = getTrackingIdentity();
		sendEvent(
			{
				...buildCommonFields(
					'wishlist_property_added',
					portalUserId,
					sessionId,
					input.propertyId,
					resolveSnapshot(input.property)
				),
				payload: { wishlistId: input.wishlistId }
			},
			logger
		);
	}

	function trackWishlistPropertyRemoved(
		input: TrackWishlistBehaviorInput
	): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId || !input.wishlistId || !input.propertyId) return;

		const { sessionId } = getTrackingIdentity();
		sendEvent(
			{
				...buildCommonFields(
					'wishlist_property_removed',
					portalUserId,
					sessionId,
					input.propertyId,
					resolveSnapshot(input.property)
				),
				payload: { wishlistId: input.wishlistId }
			},
			logger
		);
	}

	function trackLeadCreated(input: TrackLeadCreatedInput): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId || !input.leadId || !input.propertyId) return;

		const { sessionId } = getTrackingIdentity();
		sendEvent(
			{
				...buildCommonFields(
					'lead_created',
					portalUserId,
					sessionId,
					input.propertyId,
					resolveSnapshot(input.property)
				),
				payload: {
					leadId: input.leadId,
					source: input.source ?? 'property_page'
				}
			},
			logger
		);
	}

	/**
	 * Flushes the pre-login property_viewed buffer to the current user.
	 * Remaining views are attributed to whoever logs in on this browser
	 * (intentional shared-device trade-off for the offline buffer).
	 */
	function syncOfflineBehaviorViews(): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId) return;

		const views = getOfflineBehaviorViews();
		if (views.length === 0) return;

		const { sessionId } = getTrackingIdentity();

		for (const view of views) {
			sendEvent(
				{
					...buildCommonFields(
						'property_viewed',
						portalUserId,
						view.sessionId || sessionId,
						view.propertyId,
						view.propertySnapshot,
						view.occurredAt
					),
					payload: {
						viewType: view.viewType,
						source: view.source
					}
				},
				logger
			);
		}

		clearOfflineBehaviorViews();
	}

	return {
		trackPropertyViewed,
		trackWishlistPropertyAdded,
		trackWishlistPropertyRemoved,
		trackLeadCreated,
		syncOfflineBehaviorViews
	};
}
